import { curryLogger } from './utils'

/**
 * Creates and logs a Kendra search event with the provided event data.
 *
 * @param {Function} createEvent - A function to create the event object.
 * @param {object} eventData - An object containing information about the search event.
 * @param {string} eventData.category - The category of the event.
 * @param {object} eventData.data - The data to include in the event.
 */
const createKendraSearchEvent = (createEvent, eventData) => {
  createEvent({
    category: 'kendrasearch',
    data: {
      ...eventData
    }
  })
  curryLogger(
    `[ANALYTICS]: ${eventData.action}`,
    `Payload ${JSON.stringify({ ...eventData }, null, 2)}`
  )
}

/**
 * Pushes a Kendra search event with the provided parameters.
 * The function creates an event object with the appropriate data based on the `data.action` value,
 * and then calls the `createKendraSearchEvent` function to push and log the event.
 *
 * @param {Function} createEvent - A function to create the event object.
 * @param {string} queryText - The search query text.
 * @param {string} indexId - The ID of the Kendra index.
 * @param {object} data - An object containing information about the search event.
 * @param {string} data.action - The action that triggered the search event (e.g. 'submit', 'select_suggestion', 'success', 'feedback', 'revise').
 * @param {string} [data.search_term] - The search term used in the event.
 * @param {string} [data.outcome_type] - The type of outcome for the search event.
 * @param {string} [data.outcome_value] - The value of the outcome for the search event.
 * @param {string} [data.question] - The question associated with the search event.
 * @param {string} [data.link_to] - The link associated with the search event.
 */
export const pushKendraSearchEvent = (
  createEvent,
  queryText,
  indexId,
  data
) => {
  const eventData = {
    search_term: queryText,
    indexID: indexId
  }
  switch (data.action) {
    case 'submit':
      eventData.action = 'submit'
      eventData.search_term = data.search_term
      break
    case 'select_suggestion':
      eventData.action = 'select_suggestion'
      eventData.search_term = data.search_term
      break
    case 'success':
      eventData.action = 'success'
      eventData.search_term = data.search_term
      eventData.outcome_type = data.outcome_type
      eventData.outcome_value = data.outcome_value
      break
    case 'feedback':
      eventData.action = 'feedback'
      eventData.question = data.question
      eventData.link_to = data.link_to
      eventData.outcome_type = data.outcome_type
      eventData.outcome_value = data.outcome_value
      break
    case 'revise':
      eventData.action = 'revise'
      eventData.outcome_type = data.outcome_type
      eventData.outcome_value = data.outcome_value
      break
    default:
      throw new Error(`Unknown action: ${data.action}`)
  }
  return createKendraSearchEvent(createEvent, eventData)
}

/**
 * Curries the `pushKendraSearchEvent` function with the `createEvent`, `indexId`, and `queryText` parameters.
 * This allows the caller to provide the `data` parameter and get the full `pushKendraSearchEvent` call.
 * @param {Function} createEvent - A function to create the event object.
 * @param {string} indexId - The ID of the Kendra index.
 * @param {string} queryText - The search query text.
 * @returns {Function} A function that takes the `data` parameter and calls `pushKendraSearchEvent`.
 */
export const curryEventWithIdAndQuery =
  (createEvent, indexId, queryText) => data =>
    pushKendraSearchEvent(createEvent, queryText, indexId, data)